import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
  articleDate?: string;
  articleAuthor?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schema,
  articleDate,
  articleAuthor,
}: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    const fullTitle = `${title} - 韩国伦理文化研究`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl || window.location.href, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);

    if (articleDate) setMeta('article:published_time', articleDate);
    if (articleAuthor) setMeta('article:author', articleAuthor);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl || window.location.href);

    let schemaScript: HTMLScriptElement | null = null;
    if (schema) {
      const existingSchema = document.querySelectorAll('script[type="application/ld+json"]');
      const hasPageLevel = Array.from(existingSchema).some(
        (s) => s.getAttribute('data-page-level') === 'true'
      );
      if (hasPageLevel) {
        const pageSchema = document.querySelector('script[type="application/ld+json"][data-page-level="true"]');
        if (pageSchema) pageSchema.textContent = JSON.stringify(schema);
      } else {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.setAttribute('data-page-level', 'true');
        schemaScript.textContent = JSON.stringify(schema);
        document.head.appendChild(schemaScript);
      }
    }

    return () => {
      if (schemaScript && schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schema, articleDate, articleAuthor, location.pathname]);

  return null;
}